import { type IFetchTaskQuery, type ITask, TaskPendingStatuses, TaskRisk, TaskStatus } from '@condo/domain';
import type { InjectionKey, Ref } from 'vue';

export const defaultTaskFilter: IFetchTaskQuery = {
    page: 1,
    size: 50,
    order: 'DESC',
    orderBy: 'taskId',
    mfhId: undefined,
    status: TaskPendingStatuses,
    mfhBuildingId: undefined,
    mfhPortfolioId: undefined,
    estateId: undefined,
    assignee: [],
    actionType: [],
    processType: [],
    taskText: undefined,
    overDue: undefined,
};
export const mfhTaskGroups = ['Post-Closing', 'Pre-Construction', 'Construction', 'Post-Construction'];

export const taskRiskRender = (risk: TaskRisk) => {
    let icon = '';
    let color = '';

    switch (risk) {
        case TaskRisk.HighRisk:
            icon = 'circle-exclamation';
            color = 'red-700';
            break;
        case TaskRisk.LowRisk:
            icon = 'thumbs-up';
            color = 'green-700';
            break;
        case TaskRisk.MediumRisk:
            icon = 'circle-exclamation';
            color = 'yellow-500';
            break;
        case TaskRisk.NoRisk:
            icon = 'circle';
            color = 'grey-500';
            break;
        default:
            icon = 'circle';
            color = 'grey-500';
    }
    return {
        icon,
        color,
        iconGroup: 'solid',
    };
};

export interface ITaskTableProps {
    mfhId?: number;
    estateId?: number;
}

export interface ITaskItem {
    type: 'task';
    task: Partial<ITask>;
}

export interface TaskGroup {
    type: 'group';
    key: string;
    task: ITask;
    subTasks: ITaskItem[];
}

export type TaskItemOrGroup = ITaskItem | TaskGroup;

export interface GroupedTaskItems {
    groupKey: string;
    taskItems: TaskItemOrGroup[];
}

export const editTaskKey: InjectionKey<(task: Partial<ITask>) => void> = Symbol('editTask');
export const openTaskDetailKey: InjectionKey<Ref<number | null>> = Symbol('openTaskDetail');
export const allTasksKey: InjectionKey<Ref<ITask[]>> = Symbol('allTasks');

export const taskStatusColors: Record<TaskStatus, 'default' | 'error' | 'primary' | 'info' | 'success' | 'warning'> = {
    [TaskStatus.Opened]: 'default',
    [TaskStatus.Planned]: 'warning',
    [TaskStatus.WaitingForInput]: 'warning',
    [TaskStatus.InProgress]: 'warning',
    [TaskStatus.AwaitingApproval]: 'warning',
    [TaskStatus.OnHold]: 'error',
    [TaskStatus.Stopped]: 'error',
    [TaskStatus.NotRequired]: 'success',
    [TaskStatus.Completed]: 'success',
};

export const taskRiskOptions: Record<
    TaskRisk,
    {
        key: string;
        label: string;
        bgClass: string;
        value: string;
        color: 'default' | 'error' | 'success' | 'warning' | 'primary' | 'info' | undefined;
    }
> = {
    [TaskRisk.NoRisk]: {
        key: TaskRisk.NoRisk,
        label: '-',
        bgClass: 'bg-gray-300',
        color: 'default',
        value: TaskRisk.NoRisk,
    },
    [TaskRisk.LowRisk]: {
        key: TaskRisk.LowRisk,
        label: 'No Risk',
        bgClass: 'bg-success',
        color: 'success',
        value: TaskRisk.LowRisk,
    },
    [TaskRisk.MediumRisk]: {
        key: TaskRisk.MediumRisk,
        label: 'Medium Risk',
        bgClass: 'bg-warning',
        color: 'warning',
        value: TaskRisk.MediumRisk,
    },
    [TaskRisk.HighRisk]: {
        key: TaskRisk.HighRisk,
        label: 'High Risk',
        bgClass: 'bg-red',
        color: 'error',
        value: TaskRisk.HighRisk,
    },
};

/**
 * Temp Solution Until all processes are running
 * @todo
 */
export const MfhTaskStructure = {
    'Post-Closing': ['Deal closing', 'PM onboarding', 'Debt financing', 'Review and implementation of rent increases', '3D Scan and BIM model'],
    'Pre-Construction': [
        'Tenant relocation attic floor',
        'Energy assessment',
        'KfW subsidy application',
        'Construction planning',
        'Construction tender process',
        'Construction permit (application / grant)',
        'Modernization announcement to tenants',
    ],
    Construction: ['Energetic modernization (incl. strings/lines)', 'Construction - attic extension', 'Construction - roof extension'],
    'Post-construction': ['Rent increase announcement to tenants', 'KfW subsidy confirmation of implementation'],
};
